"use client";

type Variant = "light" | "dark";

const labelLight = "block text-sm font-semibold text-zinc-800";
const labelDark = "block text-sm font-semibold text-zinc-200";

const numLight =
  "min-h-11 w-full min-w-0 rounded-lg border border-zinc-200 bg-white px-2 py-2 text-sm font-semibold text-zinc-900 shadow-sm outline-none ring-violet-400 focus:ring-2 sm:min-h-12 sm:rounded-xl sm:px-3 sm:py-3 sm:text-base";
const numDark =
  "min-h-11 w-full min-w-0 rounded-lg border border-white/10 bg-zinc-900 px-2 py-2 text-sm font-semibold text-white outline-none ring-fuchsia-500/50 focus:ring-2 sm:min-h-12 sm:rounded-xl sm:px-3 sm:py-3 sm:text-base";

type Props = {
  variant: Variant;
  isCreator: boolean;
  onIsCreator: (v: boolean) => void;
  creatorIgFollowers: number;
  creatorTiktokFollowers: number;
  creatorYoutubeSubscribers: number;
  onCreatorIgFollowers: (n: number) => void;
  onCreatorTiktokFollowers: (n: number) => void;
  onCreatorYoutubeSubscribers: (n: number) => void;
  /** When set, shows avg views field below platform follower counts. */
  avgViewsPerPost?: number;
  onAvgViewsPerPost?: (n: number) => void;
};

export function CreatorPlatformFields({
  variant,
  isCreator,
  onIsCreator,
  creatorIgFollowers,
  creatorTiktokFollowers,
  creatorYoutubeSubscribers,
  onCreatorIgFollowers,
  onCreatorTiktokFollowers,
  onCreatorYoutubeSubscribers,
  avgViewsPerPost,
  onAvgViewsPerPost,
}: Props) {
  const lab = variant === "light" ? labelLight : labelDark;
  const num = variant === "light" ? numLight : numDark;
  const panel =
    variant === "light"
      ? "rounded-lg border border-violet-200/70 bg-white/60 p-3"
      : "rounded-lg border border-white/10 bg-zinc-900/50 p-3";
  const subline = variant === "light" ? "text-zinc-600" : "text-zinc-400";

  return (
    <div className="space-y-3">
      <div className={panel}>
        <p className={`text-sm font-semibold ${variant === "light" ? "text-zinc-800" : "text-zinc-200"}`}>
          Rough follower counts by platform
        </p>
        <p className={`mt-0.5 text-xs ${subline}`}>
          Optional — use 0 if you don&apos;t use a platform. Rough guesses are fine.
        </p>
        <div className="mt-3 grid grid-cols-3 gap-2 sm:gap-3">
          <div className="min-w-0 space-y-1">
            <label htmlFor="creator-ig" className={`${lab} text-[11px] sm:text-xs`}>
              IG
            </label>
            <input
              id="creator-ig"
              type="number"
              inputMode="numeric"
              min={0}
              max={999_999_999}
              value={creatorIgFollowers}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                onCreatorIgFollowers(Number.isNaN(v) ? 0 : Math.min(999_999_999, Math.max(0, v)));
              }}
              placeholder="0"
              className={num}
            />
          </div>
          <div className="min-w-0 space-y-1">
            <label htmlFor="creator-tt" className={`${lab} text-[11px] sm:text-xs`}>
              TikTok
            </label>
            <input
              id="creator-tt"
              type="number"
              inputMode="numeric"
              min={0}
              max={999_999_999}
              value={creatorTiktokFollowers}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                onCreatorTiktokFollowers(Number.isNaN(v) ? 0 : Math.min(999_999_999, Math.max(0, v)));
              }}
              placeholder="0"
              className={num}
            />
          </div>
          <div className="min-w-0 space-y-1">
            <label htmlFor="creator-yt" className={`${lab} text-[11px] sm:text-xs`}>
              YouTube
            </label>
            <input
              id="creator-yt"
              type="number"
              inputMode="numeric"
              min={0}
              max={999_999_999}
              value={creatorYoutubeSubscribers}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                onCreatorYoutubeSubscribers(Number.isNaN(v) ? 0 : Math.min(999_999_999, Math.max(0, v)));
              }}
              placeholder="0"
              className={num}
            />
          </div>
        </div>
        {onAvgViewsPerPost !== undefined ? (
          <div className="mt-3 space-y-1.5">
            <label htmlFor="creator-avg-views" className={`${lab} text-xs`}>
              Typical views per post or video
            </label>
            <input
              id="creator-avg-views"
              type="number"
              inputMode="numeric"
              min={0}
              max={999_999_999}
              value={avgViewsPerPost ?? 0}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                onAvgViewsPerPost(Number.isNaN(v) ? 0 : Math.min(999_999_999, Math.max(0, v)));
              }}
              placeholder="0"
              className={num}
            />
          </div>
        ) : null}
      </div>

      <div className={panel}>
        <label className={`flex cursor-pointer gap-3 ${variant === "light" ? "text-zinc-800" : "text-zinc-200"}`}>
          <input
            type="checkbox"
            checked={isCreator}
            onChange={(e) => onIsCreator(e.target.checked)}
            className={`mt-0.5 size-5 shrink-0 ${variant === "light" ? "accent-violet-600" : "accent-fuchsia-500"}`}
          />
          <span className="text-sm font-medium leading-snug">I&apos;m a content creator.</span>
        </label>
      </div>
    </div>
  );
}
